import { useEffect, useRef } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { useSettingsStore } from '@/stores/settings-store';
import { useNotifications } from './useNotifications';
import { apiGet } from '@/services/api-client';
import { hasStoredExpoPushToken } from '@/utils/expo-push-token';

type UnreadCounts = Record<string, { 
  unreadThreads: number; 
  newBoardNotes: number; 
  newCalendarEvents: number; 
  newWikiArticles: number;
}>;

interface Group {
  id: string;
  name: string;
  ownerId: string;
}

export function useGroupActivityNotifications() {
  const { user, accessToken } = useAuthStore();
  const { notifications: notificationSettings } = useSettingsStore();
  const { sendNotification, permissionStatus } = useNotifications();
  const previousCountsRef = useRef<UnreadCounts | null>(null);
  const seenGroupActivityRef = useRef<Set<string>>(new Set());
  const intervalIdRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!notificationSettings.groupActivity || !permissionStatus.granted || !accessToken || !user?.id) {
      return;
    }

    const checkForGroupActivity = async () => {
      // Check for push token before each poll - stop polling if token exists
      const hasPushToken = hasStoredExpoPushToken();
      if (hasPushToken) {
        console.log('[useGroupActivityNotifications] Push token detected, stopping polling');
        if (intervalIdRef.current) {
          clearInterval(intervalIdRef.current);
          intervalIdRef.current = null;
        }
        return;
      }

      try {
        // Fetch unread counts
        const countsResponse = await apiGet<UnreadCounts>('/groups/unread-counts');
        if (!countsResponse.success || !countsResponse.data) {
          return;
        }

        const currentCounts = countsResponse.data;

        // Fetch groups to get names
        const groupsResponse = await apiGet<Group[]>('/groups');
        if (!groupsResponse.success || !groupsResponse.data) {
          return;
        }

        const groups = groupsResponse.data;
        const groupMap = new Map(groups.map(g => [g.id, g]));

        // On first run, mark all as seen (prevent notification spam on startup)
        if (previousCountsRef.current === null) {
          previousCountsRef.current = currentCounts;
          // Mark all current group activities as seen
          Object.keys(currentCounts).forEach(groupId => {
            const counts = currentCounts[groupId];
            const totalActivity = (counts.unreadThreads || 0) + 
                                 (counts.newBoardNotes || 0) + 
                                 (counts.newCalendarEvents || 0) + 
                                 (counts.newWikiArticles || 0);
            if (totalActivity > 0) {
              seenGroupActivityRef.current.add(groupId);
            }
          });
          return;
        }

        // Check each group for new activity
        for (const [groupId, currentGroupCounts] of Object.entries(currentCounts)) {
          const previousGroupCounts = previousCountsRef.current[groupId];
          const group = groupMap.get(groupId);
          
          if (!group) continue;

          const currentTotal = (currentGroupCounts.unreadThreads || 0) + 
                              (currentGroupCounts.newBoardNotes || 0) + 
                              (currentGroupCounts.newCalendarEvents || 0) + 
                              (currentGroupCounts.newWikiArticles || 0);

          const previousTotal = previousGroupCounts 
            ? (previousGroupCounts.unreadThreads || 0) + 
              (previousGroupCounts.newBoardNotes || 0) + 
              (previousGroupCounts.newCalendarEvents || 0) + 
              (previousGroupCounts.newWikiArticles || 0)
            : 0;

          // New activity detected
          if (currentTotal > previousTotal && currentTotal > 0) {
            const activityKey = `${groupId}-${currentTotal}`;
            
            if (!seenGroupActivityRef.current.has(activityKey)) {
              seenGroupActivityRef.current.add(activityKey);

              // Build activity description
              const activities: string[] = [];
              
              const newThreads = (currentGroupCounts.unreadThreads || 0) - (previousGroupCounts?.unreadThreads || 0);
              const newNotes = (currentGroupCounts.newBoardNotes || 0) - (previousGroupCounts?.newBoardNotes || 0);
              const newEvents = (currentGroupCounts.newCalendarEvents || 0) - (previousGroupCounts?.newCalendarEvents || 0);
              const newArticles = (currentGroupCounts.newWikiArticles || 0) - (previousGroupCounts?.newWikiArticles || 0);

              if (newThreads > 0) activities.push(`${newThreads} ny${newThreads > 1 ? 'a' : ''} diskussion${newThreads > 1 ? 'er' : ''}`);
              if (newNotes > 0) activities.push(`${newNotes} ny${newNotes > 1 ? 'a' : 'tt'} inlägg`);
              if (newEvents > 0) activities.push(`${newEvents} ny${newEvents > 1 ? 'a' : 'tt'} kalender${newEvents > 1 ? 'händelser' : 'händelse'}`);
              if (newArticles > 0) activities.push(`${newArticles} ny${newArticles > 1 ? 'a' : ''} wiki${newArticles > 1 ? 'artiklar' : 'artikel'}`);

              const activityText = activities.length > 0 
                ? activities.join(', ')
                : 'nya uppdateringar';

              sendNotification({
                title: 'Gruppaktivitet',
                body: `${group.name} har ${activityText}`,
                data: {
                  type: 'group-activity',
                  url: `/app/groups/${groupId}`,
                  groupId
                }
              });
            }
          }
        }

        // Update previous counts
        previousCountsRef.current = currentCounts;

        // Clean up seen activities set (keep last 100)
        if (seenGroupActivityRef.current.size > 100) {
          const entries = Array.from(seenGroupActivityRef.current);
          seenGroupActivityRef.current = new Set(entries.slice(-100));
        }

      } catch (error) {
        console.error('Error checking group activity:', error);
      }
    };

    // Initial check
    checkForGroupActivity();

    // Poll every 30 seconds
    intervalIdRef.current = setInterval(checkForGroupActivity, 30 * 1000);

    return () => {
      if (intervalIdRef.current) {
        clearInterval(intervalIdRef.current);
      }
    };
  }, [notificationSettings.groupActivity, permissionStatus.granted, accessToken, user?.id, sendNotification]);
}
